import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';

import ToggleSwitch from '@/components/inputs/ToggleSwitch';

/**
 * Editor de JSON interactivo para las reglas de ruteo.
 *
 * Recibe y devuelve un string JSON (misma interfaz que el antiguo textarea),
 * por lo que encaja sin tocar el guardado/validación del hook padre.
 *
 * Internamente mantiene un árbol con ids estables y guarda los primitivos como
 * texto en edición; sólo al serializar se coacciona al tipo JSON real. Esto
 * evita perder el foco al renombrar claves y los saltos típicos de los inputs
 * numéricos controlados.
 */

// 'color' es un subtipo de string (se serializa como string); existe solo para
// mostrar un selector de color cuando el valor es un hex.
type PrimitiveType = 'string' | 'number' | 'boolean' | 'null' | 'color';
type NodeType = PrimitiveType | 'object' | 'array';

interface Entry {
  id: string;
  key: string;
  child: TreeNode;
}

interface TreeNode {
  id: string;
  type: NodeType;
  raw?: string; // string y number (texto en edición)
  bool?: boolean; // boolean
  entries?: Entry[]; // object
  items?: TreeNode[]; // array
}

let idCounter = 0;
const nextId = () => `n${idCounter++}`;

const TYPE_OPTIONS: { value: PrimitiveType; label: string }[] = [
  { value: 'string', label: 'texto' },
  { value: 'number', label: 'número' },
  { value: 'boolean', label: 'bool' },
  { value: 'color', label: 'color' },
  { value: 'null', label: 'vacío' },
];

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim());

/** Normaliza a #rrggbb para el <input type="color"> (que no acepta #rgb). */
const normalizeHex = (raw?: string): string => {
  const value = (raw ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
  if (/^#[0-9a-fA-F]{3}$/.test(value)) {
    return `#${value
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('')}`;
  }
  return '#000000';
};

// ---- Conversión JSON <-> árbol -------------------------------------------

const jsonToTree = (value: unknown): TreeNode => {
  if (value === null || value === undefined) {
    return { id: nextId(), type: 'null' };
  }
  if (Array.isArray(value)) {
    return { id: nextId(), type: 'array', items: value.map(jsonToTree) };
  }
  switch (typeof value) {
    case 'boolean':
      return { id: nextId(), type: 'boolean', bool: value };
    case 'number':
      return { id: nextId(), type: 'number', raw: String(value) };
    case 'string':
      return isHexColor(value)
        ? { id: nextId(), type: 'color', raw: value }
        : { id: nextId(), type: 'string', raw: value };
    case 'object':
      return {
        id: nextId(),
        type: 'object',
        entries: Object.entries(value as Record<string, unknown>).map(
          ([key, val]) => ({ id: nextId(), key, child: jsonToTree(val) })
        ),
      };
    default:
      return { id: nextId(), type: 'string', raw: String(value) };
  }
};

const treeToJson = (node: TreeNode): unknown => {
  switch (node.type) {
    case 'null':
      return null;
    case 'boolean':
      return node.bool ?? false;
    case 'number': {
      const parsed = Number((node.raw ?? '').trim());
      return Number.isFinite(parsed) ? parsed : 0;
    }
    case 'string':
    case 'color':
      return node.raw ?? '';
    case 'array':
      return (node.items ?? []).map(treeToJson);
    case 'object':
      return (node.entries ?? []).reduce<Record<string, unknown>>(
        (acc, entry) => {
          acc[entry.key] = treeToJson(entry.child);
          return acc;
        },
        {}
      );
  }
};

const asString = (node: TreeNode): string => {
  if (node.type === 'boolean') return node.bool ? 'true' : 'false';
  return node.raw ?? '';
};

const asBool = (node: TreeNode): boolean => {
  if (node.type === 'boolean') return !!node.bool;
  const raw = (node.raw ?? '').trim().toLowerCase();
  if (node.type === 'number') return Number(raw) !== 0;
  return raw !== '' && raw !== 'false' && raw !== '0';
};

const convert = (node: TreeNode, newType: PrimitiveType): TreeNode => {
  if (node.type === newType) return node;
  const base = { id: node.id };
  switch (newType) {
    case 'string':
      return { ...base, type: 'string', raw: asString(node) };
    case 'number':
      return { ...base, type: 'number', raw: asString(node) };
    case 'boolean':
      return { ...base, type: 'boolean', bool: asBool(node) };
    case 'color': {
      const current = asString(node);
      return {
        ...base,
        type: 'color',
        raw: isHexColor(current) ? current : '#000000',
      };
    }
    case 'null':
      return { ...base, type: 'null' };
  }
};

// ---- Acciones inmutables sobre el árbol -----------------------------------

type Action =
  | { kind: 'setRaw'; id: string; raw: string }
  | { kind: 'setBool'; id: string; bool: boolean }
  | { kind: 'setType'; id: string; newType: PrimitiveType }
  | { kind: 'renameKey'; entryId: string; key: string }
  | { kind: 'deleteEntry'; entryId: string }
  | { kind: 'deleteItem'; itemId: string }
  | { kind: 'addField'; objId: string }
  | { kind: 'addItem'; arrId: string };

const newField = (): Entry => ({
  id: nextId(),
  key: '',
  child: { id: nextId(), type: 'string', raw: '' },
});

const apply = (node: TreeNode, action: Action): TreeNode => {
  // Acciones que apuntan a un nodo concreto.
  if ('id' in action && node.id === action.id) {
    if (action.kind === 'setRaw') return { ...node, raw: action.raw };
    if (action.kind === 'setBool') return { ...node, bool: action.bool };
    if (action.kind === 'setType') return convert(node, action.newType);
  }

  if (node.type === 'object') {
    let entries = node.entries ?? [];
    if (action.kind === 'renameKey') {
      entries = entries.map((e) =>
        e.id === action.entryId ? { ...e, key: action.key } : e
      );
    }
    if (action.kind === 'deleteEntry') {
      entries = entries.filter((e) => e.id !== action.entryId);
    }
    if (action.kind === 'addField' && node.id === action.objId) {
      entries = [...entries, newField()];
    }
    entries = entries.map((e) => ({ ...e, child: apply(e.child, action) }));
    return { ...node, entries };
  }

  if (node.type === 'array') {
    let items = node.items ?? [];
    if (action.kind === 'deleteItem') {
      items = items.filter((i) => i.id !== action.itemId);
    }
    if (action.kind === 'addItem' && node.id === action.arrId) {
      items = [...items, { id: nextId(), type: 'string', raw: '' }];
    }
    items = items.map((i) => apply(i, action));
    return { ...node, items };
  }

  return node;
};

// ---- Parseo seguro --------------------------------------------------------

const safeParse = (value: string): { tree: TreeNode | null; error: string | null } => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { tree: { id: nextId(), type: 'object', entries: [] }, error: null };
  }
  try {
    return { tree: jsonToTree(JSON.parse(trimmed)), error: null };
  } catch (e) {
    return { tree: null, error: (e as Error).message };
  }
};

// ---- UI --------------------------------------------------------------------

type InteractiveConfigEditorProps = {
  value: string;
  error: string | null;
  onChange: (value: string) => void;
};

const InteractiveConfigEditor = ({
  value,
  error,
  onChange,
}: InteractiveConfigEditorProps) => {
  const [{ tree, error: parseError }, setState] = useState(() =>
    safeParse(value)
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const lastEmitted = useRef(value);

  // Sincroniza con cambios externos (carga de config, restaurar, limpiar).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setState(safeParse(value));
    }
  }, [value]);

  const emit = (nextTree: TreeNode) => {
    setState({ tree: nextTree, error: null });
    const json = JSON.stringify(treeToJson(nextTree), null, 2);
    lastEmitted.current = json;
    onChange(json);
  };

  const dispatch = (action: Action) => {
    if (!tree) return;
    emit(apply(tree, action));
  };

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const displayError = parseError || error;

  const previewJson = useMemo(
    () => (tree ? JSON.stringify(treeToJson(tree), null, 2) : value),
    [tree, value]
  );

  if (!tree) {
    // Fallback: JSON inválido -> textarea plano para no dejar al usuario atascado.
    return (
      <div className='flex flex-col gap-3'>
        <p className='text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2'>
          El JSON no se pudo interpretar como editor visual. Corrígelo aquí:
        </p>
        <textarea
          className='w-full min-h-[300px] font-mono text-sm border border-gray-200 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-orange-500'
          value={value}
          onChange={(event) => {
            lastEmitted.current = event.target.value;
            onChange(event.target.value);
          }}
          spellCheck={false}
        />
        {displayError && (
          <p className='text-sm text-red-600'>{displayError}</p>
        )}
      </div>
    );
  }

  const renderValueControl = (node: TreeNode) => {
    switch (node.type) {
      case 'boolean':
        return (
          <ToggleSwitch
            checked={!!node.bool}
            onChange={(bool) => dispatch({ kind: 'setBool', id: node.id, bool })}
            ariaLabel='valor booleano'
          />
        );
      case 'number':
        return (
          <input
            type='text'
            inputMode='decimal'
            className='w-[240px] rounded-md border border-gray-200 px-3 py-2 font-mono text-base text-blue-700 focus:outline-none focus:ring-2 focus:ring-orange-400'
            value={node.raw ?? ''}
            onChange={(e) =>
              dispatch({ kind: 'setRaw', id: node.id, raw: e.target.value })
            }
          />
        );
      case 'string':
        return (
          <input
            type='text'
            className='w-[420px] max-w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-base text-green-700 focus:outline-none focus:ring-2 focus:ring-orange-400'
            value={node.raw ?? ''}
            onChange={(e) =>
              dispatch({ kind: 'setRaw', id: node.id, raw: e.target.value })
            }
          />
        );
      case 'color':
        return (
          <div className='flex items-center gap-2'>
            <input
              type='color'
              className='h-10 w-12 shrink-0 cursor-pointer rounded-md border border-gray-200 bg-white p-1'
              value={normalizeHex(node.raw)}
              onChange={(e) =>
                dispatch({ kind: 'setRaw', id: node.id, raw: e.target.value })
              }
              aria-label='selector de color'
            />
            <input
              type='text'
              className='w-[160px] rounded-md border border-gray-200 px-3 py-2 font-mono text-base text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400'
              value={node.raw ?? ''}
              onChange={(e) =>
                dispatch({ kind: 'setRaw', id: node.id, raw: e.target.value })
              }
              spellCheck={false}
            />
          </div>
        );
      case 'null':
        return <span className='font-mono text-base text-gray-400'>null</span>;
      default:
        return null;
    }
  };

  const renderTypeSelect = (node: TreeNode) => (
    <select
      value={node.type}
      onChange={(e) =>
        dispatch({
          kind: 'setType',
          id: node.id,
          newType: e.target.value as PrimitiveType,
        })
      }
      className='shrink-0 rounded-md bg-gray-100 px-2 py-1.5 text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400'
      aria-label='tipo de dato'
    >
      {TYPE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const iconBtn =
    'shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors';
  const trashBtn =
    'shrink-0 rounded p-0.5 text-gray-300 opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-500';

  const renderKeyControl = (entry: Entry) => (
    <input
      type='text'
      className='w-[240px] shrink-0 rounded-md border border-transparent bg-transparent px-2 py-2 font-mono text-base font-medium text-gray-800 hover:border-gray-200 focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-300'
      value={entry.key}
      onChange={(e) =>
        dispatch({ kind: 'renameKey', entryId: entry.id, key: e.target.value })
      }
      spellCheck={false}
    />
  );

  // Renderiza un nodo. `label` = control de clave (objeto) o índice (array).
  const renderNode = (
    node: TreeNode,
    depth: number,
    label: React.ReactNode,
    onDelete?: () => void
  ): React.ReactNode => {
    const indent = { paddingLeft: `${depth * 18}px` };
    const isContainer = node.type === 'object' || node.type === 'array';

    if (!isContainer) {
      return (
        <div
          key={node.id}
          style={indent}
          className='group flex items-center gap-2 rounded-md py-1 pr-2 hover:bg-white'
        >
          {label}
          <span className='select-none text-gray-300'>:</span>
          {renderValueControl(node)}
          {renderTypeSelect(node)}
          {onDelete && (
            <button
              type='button'
              onClick={onDelete}
              className={trashBtn}
              aria-label='eliminar campo'
            >
              <DeleteIcon fontSize='inherit' style={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      );
    }

    const isCollapsed = collapsed.has(node.id);
    const count =
      node.type === 'object'
        ? node.entries?.length ?? 0
        : node.items?.length ?? 0;
    const typeLabel = node.type === 'object' ? `objeto` : `lista`;

    return (
      <div key={node.id}>
        <div
          style={indent}
          className='group flex items-center gap-1.5 rounded-md py-1 pr-2 hover:bg-white'
        >
          <button
            type='button'
            onClick={() => toggleCollapse(node.id)}
            className={iconBtn}
            aria-label={isCollapsed ? 'expandir' : 'colapsar'}
          >
            {isCollapsed ? (
              <CollapseIcon fontSize='inherit' style={{ fontSize: 20 }} />
            ) : (
              <ExpandIcon fontSize='inherit' style={{ fontSize: 20 }} />
            )}
          </button>
          {label}
          <span className='shrink-0 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-500'>
            {typeLabel} · {count}
          </span>
          <button
            type='button'
            onClick={() =>
              dispatch(
                node.type === 'object'
                  ? { kind: 'addField', objId: node.id }
                  : { kind: 'addItem', arrId: node.id }
              )
            }
            className={iconBtn}
            aria-label='agregar'
          >
            <AddIcon fontSize='inherit' style={{ fontSize: 18 }} />
          </button>
          {onDelete && (
            <button
              type='button'
              onClick={onDelete}
              className={trashBtn}
              aria-label='eliminar'
            >
              <DeleteIcon fontSize='inherit' style={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div>
            {node.type === 'object'
              ? (node.entries ?? []).map((entry) =>
                  renderNode(
                    entry.child,
                    depth + 1,
                    renderKeyControl(entry),
                    () => dispatch({ kind: 'deleteEntry', entryId: entry.id })
                  )
                )
              : (node.items ?? []).map((item, index) =>
                  renderNode(
                    item,
                    depth + 1,
                    <span className='shrink-0 font-mono text-sm text-gray-400'>
                      {index}
                    </span>,
                    () => dispatch({ kind: 'deleteItem', itemId: item.id })
                  )
                )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='flex flex-col gap-3'>
      <p className='text-xs text-gray-500'>
        Edita cada valor directamente. La etiqueta junto al valor indica su tipo.
      </p>

      <div className='rounded-lg border border-gray-200 bg-gray-50/50 p-3'>
        {renderNode(tree, 0, <span className='sr-only'>raíz</span>)}
      </div>

      {displayError && (
        <p className='text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2'>
          {displayError}
        </p>
      )}

      <details className='text-xs text-gray-500'>
        <summary className='cursor-pointer select-none hover:text-gray-700'>
          Ver JSON
        </summary>
        <pre className='mt-2 max-h-64 overflow-auto rounded bg-gray-900 p-3 font-mono text-xs text-gray-100'>
          {previewJson}
        </pre>
      </details>
    </div>
  );
};

export default InteractiveConfigEditor;
